import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { setAuthToken } from '../utils/apiUtils';
import { login } from '../services/authService';
import {
  Container,
  BackgroundAnimation,
  FactoryFloorIframe,
  GridOverlay,
  HexagonPattern,
  LoginContainer,
  GlowEffect,
  BrandSection,
  BrandTitle,
  BrandTagline,
  CircleDecoration,
  LoginSection,
  LoginTitle,
  LoginSubtitle,
  BiometricContainer,
  BiometricOptionsContainer,
  BiometricOption,
  MoreOptionsButton,
  MoreOptionsPanel,
  TabContainer,
  Tab,
  Form,
  InputGroup,
  Label,
  Input,
  Button,
  PinContainer,
  PinInput,
  BiometricCircle,
  BiometricIcon,
  BiometricLabel,
  VideoFeed,
  BiometricButton,
  StatusText,
  FaceGrid,
  GridCell,
  VoiceVisualizer,
  VoiceLine,
  AlternativeLogin,
  Footer,
  Copyright,
  TwoFactorModal,
  TwoFactorCard,
  TwoFactorTitle,
  TwoFactorDescription,
  ButterflyContainer,
  ButterflyLeftWing,
  ButterflyRightWing,
  DataStream,
  DataLine,
  DigitalLogo,
  LogoRing,
  LogoInnerCircle,
  LogoDot,
  LogoLine,
  AgentBotContainer,
  AgentBotHead,
  AgentBotLeftEye,
  AgentBotRightEye,
  AgentBotMouth,
  AgentBotBody,
  AgentBotLeftArm,
  AgentBotRightArm,
  AgentBotLeftLeg,
  AgentBotRightLeg,
  AgentBotLight,
  AgentBotCircuit,
  AgentBotPulse,
  OauthProviders
} from '../styles/LoginStyles';
import { fetchOAuthConfigs, processOAuthCallback } from '../services/oauthService';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { provider: urlProvider } = useParams();
  const videoRef = useRef(null);
  const [pinRefs, setPinRefs] = useState({ current: Array(6).fill(null) });
  const [authMethod, setAuthMethod] = useState('password'); // Default to face recognition
  const [status, setStatus] = useState('');
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorMethod, setTwoFactorMethod] = useState('app'); // 'app', 'sms', 'email'
  const [isFaceScanning, setIsFaceScanning] = useState(false);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [pinDigits, setPinDigits] = useState(Array(6).fill(''));
  const [videoStream, setVideoStream] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [voiceVisualizerData, setVoiceVisualizerData] = useState(Array(20).fill(10));

  const [oauthProviders, setOauthProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const APPLICATION_ID = 'BLUEMORMON';
  const APPLICATION_SOURCE = 'BLUEMORMON';


  const [dataLines, setDataLines] = useState(() => {
    return Array.from({ length: 15 }, (_, i) => ({
      id: i,
      text: Array.from({ length: Math.floor(Math.random() * 20) + 10 }, () =>
        Math.random() > 0.5 ? '1' : '0'
      ).join(''),
      x: Math.random() * 100,
      y: Math.random() * 100,
      speed: Math.random() * 10 + 5,
      delay: Math.random() * 5
    }));
  });
  const [authParams, setAuthParams] = useState({
    code: null,
    provider: null,
    state: null,
    applicationId: 'BLUEMORMON',
    applicationSource: 'BLUEMORMON'
  });

  // OAUTH
  // Parse URL parameters when redirected back from OAuth provider
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get('code');
    const state = queryParams.get('state');
    const provider = queryParams.get('provider') || localStorage.getItem('oauth_provider');

    if (code && state) {
      setAuthParams(prev => ({
        ...prev,
        code,
        provider,
        state
      }));
    }
  }, [location]);

  // Load OAuth providers
  useEffect(() => {
    const loadOAuthProviders = async () => {
      try {
        setIsLoading(true);
        const hostname = window.location.hostname;
        const domainPrefix = hostname.split('.')[0];
        const providers = await fetchOAuthConfigs(domainPrefix);
        if (Array.isArray(providers)) {
          setOauthProviders(providers);
        }
      } catch (err) {
        setError('Failed to load login options. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadOAuthProviders();
  }, [location]);

  const handleProviderClick = (provider) => {
    // Save provider info if needed for later
    localStorage.setItem('selected_oauth_provider', provider.server_name);
    // Redirect to the OAuth provider's login page
    window.location.href = provider.uri;
  };

  // Handle OAuth callback from provider

  // Handle OAuth callback
  useEffect(() => {
    // Check if this is an OAuth callback (has a provider parameter and a code)
    const isOAuthCallback = urlProvider && location.search.includes('code=');

    if (isOAuthCallback) {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');

      console.log('OAuth callback detected!', {
        provider: urlProvider,
        codeLength: code ? code.length : 0,
        state
      });

      if (code && state) {
        handleOAuthCallback(code, state, urlProvider);
      }
    }
  }, [urlProvider, location.search]);

  // Process the OAuth callback
  const handleOAuthCallback = async (code, state, provider) => {
    try {
      setIsLoading(true);
      const authData = await processOAuthCallback(
        code, state, provider, APPLICATION_ID, APPLICATION_SOURCE
      );

      if (authData && authData.access_token) {
        // Store the token and user data
        localStorage.setItem('authToken', authData.access_token);
        setAuthToken(`${authData.access_token}`)
        // Redirect to dashboard or home page
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        setError('Authentication failed. Please try again.');
        navigate('/login');
      }
    } catch (err) {
      setError('Authentication failed. Please try again.');
      navigate('/login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCallbackResponse = (detail) => {
    // Handle the OAuth response
    console.log('OAuth callback received:', detail);

    if (detail && detail.user && detail.user.token) {
      // Store the bearer token
      localStorage.setItem('auth_token', detail.user.token);

      // Redirect to dashboard or home page
      navigate('/dashboard');
    }
  };

  const handleProviderSelection = (detail) => {
    // When user clicks on an OAuth provider in the graph
    console.log('Provider selected:', detail);
    if (detail && detail.provider) {
      localStorage.setItem('oauth_provider', detail.provider);
    }
  };

  // OAUTH END

  // For bot animations
  const [botLights, setBotLights] = useState(() =>
    Array.from({ length: 5 }, (_, i) => ({
      id: i,
      top: 20 + Math.random() * 60,
      left: 20 + Math.random() * 20,
      blinkDelay: Math.random() * 3
    }))
  );

  const [botPulses, setBotPulses] = useState(() =>
    Array.from({ length: 3 }, (_, i) => ({
      id: i,
      delay: i * 2
    }))
  );

  // Handle login form submission
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setStatus('Logging in...');
    
    try {
      // Call the login function from authService
      const response = await login(username, password);
      
      setStatus('Login successful!');
      
      // Check if two-factor authentication is required
      if (response.requires_2fa) {
        setShowTwoFactor(true);
      } else {
        // If no 2FA required, redirect to dashboard
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      }
    } catch (error) {
      console.error('Login failed:', error);
      setStatus(`Login failed: ${error.message || 'Please check your credentials'}`);
    }
  };

  useEffect(() => {
    console.log("Clearing cache and local storage...");
    localStorage.clear(); // Clear all local storage items
    sessionStorage.clear(); // Clear session storage if needed
    caches.keys().then((cacheNames) => {
      cacheNames.forEach((cacheName) => {
        caches.delete(cacheName); // Clear browser cache
      });
    });
  }, []);


  // Generate random data stream lines for the background
  useEffect(() => {
    const generateDataLines = () => {
      const lines = [];
      const characters = '01010101110000111101010111001010101010111110000';
      
      for (let i = 0; i < 50; i++) {
        const line = {
          id: i,
          text: '',
          x: Math.random() * 100,
          y: Math.random() * 100,
          speed: 0.5 + Math.random() * 2,
          delay: Math.random() * 5
        };
        
        // Generate random binary/hex text
        let text = '';
        for (let j = 0; j < 30; j++) {
          text += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        line.text = text;
        
        lines.push(line);
      }
      
      setDataLines(lines);
    };
    
    generateDataLines();
  }, []);

  // Generate bot lights and pulses
  useEffect(() => {
    // Generate random lights for the bot body
    const lights = [];
    for (let i = 0; i < 5; i++) {
      lights.push({
        id: i,
        top: 20 + Math.random() * 60,
        left: 20 + Math.random() * 20,
        blinkDelay: Math.random() * 3
      });
    }
    setBotLights(lights);

    // Generate pulses
    const pulses = [];
    for (let i = 0; i < 3; i++) {
      pulses.push({
        id: i,
        delay: i * 2
      });
    }
    setBotPulses(pulses);
  }, []);

  // Clean up audio and video streams when component unmounts
  useEffect(() => {
    return () => {
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
      }
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [audioStream, videoStream]);

  // Clean up streams when changing auth method
  useEffect(() => {
    if (authMethod !== 'voice' && audioStream) {
      audioStream.getTracks().forEach(track => track.stop());
      setAudioStream(null);
      setIsVoiceRecording(false);
    }
    
    if (authMethod !== 'face' && videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
      setIsFaceScanning(false);
    }
  }, [authMethod, audioStream, videoStream]);

  const handlePinChange = (index, value) => {
    if (value.length <= 1) {
      const newPinDigits = [...pinDigits];
      newPinDigits[index] = value;
      setPinDigits(newPinDigits);
      
      // Auto focus on next input
      if (value !== '' && index < 5) {
        pinRefs.current[index + 1].focus();
      }
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && pinDigits[index] === '' && index > 0) {
      pinRefs.current[index - 1].focus();
    }
  };

  const handleLogin = (e) => {
    if (e) e.preventDefault();
    setStatus('Authenticating...');
    
    // Simulate login and show 2FA
    setTimeout(() => {
      setStatus('First factor verified!');
      setTimeout(() => {
        setShowTwoFactor(true);
      }, 1000);
    }, 2000);
  };

  const handleTwoFactorSubmit = (e) => {
    if (e) e.preventDefault();
    
    setStatus('Verifying code...');
    
    // Validate 2FA code
    if (twoFactorCode === '123456' || twoFactorCode.length === 6) {
      // Simulate verification process
      setTimeout(() => {
        setStatus('Code verified!');
        setTimeout(() => {
          setShowTwoFactor(false);
          setStatus('Login successful!');
          setTimeout(() => {
            navigate('/dashboard');
          }, 1000);
        }, 1000);
      }, 1500);
    } else {
      setStatus('Invalid code. Please try again.');
    }
  };

  const startVoiceRecognition = () => {
    setStatus('Initializing microphone...');
    
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then(stream => {
          setAudioStream(stream);
          setIsVoiceRecording(true);
          setStatus('Listening... Please speak now');
          
          // Simulate voice recognition with random visualizer data
          const visualizerInterval = setInterval(() => {
            const newData = Array(20).fill().map(() => Math.random() * 50 + 5);
            setVoiceVisualizerData(newData);
          }, 100);
          
          // Simulate voice recognition process
          setTimeout(() => {
            setStatus('Analyzing voice pattern...');
            
            setTimeout(() => {
              clearInterval(visualizerInterval);
              
              // Stop microphone
              stream.getTracks().forEach(track => track.stop());
              setAudioStream(null);
              setIsVoiceRecording(false);
              
              // Complete authentication
              setStatus('Voice recognized!');
              setTimeout(() => {
                setShowTwoFactor(true);
              }, 1000);
            }, 3000);
          }, 3000);
        })
        .catch(err => {
          console.error('Error accessing microphone:', err);
          setStatus('Error accessing microphone. Please check permissions.');
        });
    } else {
      setStatus('Voice recognition not supported on this device');
    }
  };
  
  const startFaceRecognition = async () => {
    try {
      setIsFaceScanning(true);
      setStatus('Initializing camera...');
      
      // Request camera access with specific constraints for better face recognition
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user",
          frameRate: { ideal: 30 }
        } 
      });
      
      setVideoStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setStatus('Camera activated. Scanning face...');
        };
      }
      
      // Simulate face recognition process with visual feedback
      let scanProgress = 0;
      const scanInterval = setInterval(() => {
        scanProgress += 20;
        setStatus(`Scanning face... ${scanProgress}%`);
        
        if (scanProgress >= 100) {
          clearInterval(scanInterval);
          setStatus('Face recognized! Authenticating...');
          
          setTimeout(() => {
            setIsFaceScanning(false);
            handleLogin();
          }, 1000);
        }
      }, 600);
      
      return () => {
        clearInterval(scanInterval);
      };
      
    } catch (error) {
      console.error('Error accessing camera:', error);
      setStatus(`Camera access denied: ${error.message}`);
      setIsFaceScanning(false);
    }
  };
  
  const startPinAuthentication = () => {
    setStatus('Enter your 6-digit PIN');
  };
  
  const submitPin = () => {
    const pin = pinDigits.join('');
    if (pin.length === 6) {
      setStatus('Verifying PIN...');
      
      // Simulate PIN verification
      setTimeout(() => {
        setStatus('PIN verified!');
        handleLogin();
      }, 1500);
    } else {
      setStatus('Please enter all 6 digits');
    }
  };
  
  // Initialize PIN input refs
  useEffect(() => {
    pinRefs.current = pinRefs.current.slice(0, 6);
  }, [pinRefs]);

  const [authToken, setAuthToken] = useState('');

  const handleTwoFactorSubmitUpdated = (e) => {
    if (e) e.preventDefault();
    
    setStatus('Verifying code...');
    
    // Validate 2FA code
    if (twoFactorCode === '123456' || twoFactorCode.length === 6) {
      // Simulate verification process
      setTimeout(() => {
        setStatus('Code verified!');
        setTimeout(() => {
          setShowTwoFactor(false);
          setStatus('Login successful!');
          setTimeout(() => {
            const mockAuthResponse = {
              token: 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyLCJleHAiOjE4OTc3OTY0NDF9.z63C7DBn1AwC4Cu_yefT6NqDIPakuJr2njUGq0Fe04s',
              user: {
                id: 1,
                name: 'Test User'
              }
            };

            // Store the token in localStorage
            localStorage.setItem('authToken', mockAuthResponse.token);

            // Update state
            setAuthToken(mockAuthResponse.token);
            navigate('/dashboard');
          }, 1000);
        }, 1000);
      }, 1500);
    } else {
      setStatus('Invalid code. Please try again.');
    }
  };

  return (
    <Container>
      {/* Background elements */}
      <BackgroundAnimation>
        <FactoryFloorIframe src="/images/factory-floor.html" title="Factory Floor Animation" />
        <GridOverlay />
        <HexagonPattern />

        {/* Data stream animation */}
        <DataStream>
          {dataLines.map(line => (
            <DataLine
              key={line.id}
              style={{
                left: `${line.x}%`,
                top: `${line.y}%`,
                animationDuration: `${line.speed}s`,
                animationDelay: `${line.delay}s`
              }}
            >
              {line.text}
            </DataLine>
          ))}
        </DataStream>
      </BackgroundAnimation>

      {/* Glowing effect */}
      <GlowEffect style={{ top: '30%', left: '20%' }} />
      <GlowEffect style={{ top: '60%', left: '70%' }} />

      {/* Main login container */}
      <LoginContainer>
        {/* Brand section */}
        <BrandSection>
          <BrandTitle>
            <img
              src="/images/logo/bmm.png"
              alt="BlueMorMon Logo"
              style={{
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                position: 'relative',
                top: "-10px"
              }}
            />
          </BrandTitle>
          <BrandTagline>
            The Future of Insurance Runs on Intelligent Agentic AI
          </BrandTagline>

          <CircleDecoration
            style={{ width: '300px', height: '300px', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
            animate={{
              scale: [1, 1.05, 1],
              rotate: [0, 360]
            }}
            transition={{
              duration: 60,
              repeat: Infinity,
              ease: "linear"
            }}
          />

          <CircleDecoration
            style={{ width: '200px', height: '200px', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
            animate={{
              scale: [1, 1.1, 1],
              rotate: [360, 0]
            }}
            transition={{
              duration: 40,
              repeat: Infinity,
              ease: "linear"
            }}
          />

          <CircleDecoration
            style={{ width: '100px', height: '100px', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
            animate={{
              scale: [1, 1.15, 1],
              rotate: [0, 360]
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />

          {/* Digital Logo */}
          <DigitalLogo>
            <LogoRing
              animate={{
                rotate: [0, 360]
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            <LogoInnerCircle />
            <LogoDot
              style={{ top: '25%', left: '25%' }}
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            />
            <LogoDot
              style={{ top: '25%', left: '75%' }}
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            />
            <LogoDot
              style={{ top: '75%', left: '25%' }}
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            />
            <LogoDot
              style={{ top: '75%', left: '75%' }}
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            />
            <LogoLine
              style={{ width: '50%', height: '2px', top: '50%', left: '25%' }}
              animate={{
                opacity: [0.3, 0.8, 0.3]
              }}
              transition={{
                duration: 2,
                repeat: Infinity
              }}
            />
            <LogoLine
              style={{ width: '2px', height: '50%', top: '25%', left: '50%' }}
              animate={{
                opacity: [0.3, 0.8, 0.3]
              }}
              transition={{
                duration: 3,
                repeat: Infinity
              }}
            />
          </DigitalLogo>

          <div className="butterfly" id="fly"><i></i><i></i><i></i><i></i></div>
        </BrandSection>

        {/* Login section */}
        <LoginSection>
          <LoginTitle>Welcome Back </LoginTitle>
          <LoginSubtitle>Please authenticate to access your workspace</LoginSubtitle>

          {/* Biometric authentication options */}
          {/* <BiometricOptionsContainer>
            <BiometricOption 
              active={authMethod === 'voice' ? "true" : "false"}
              onClick={() => setAuthMethod('voice')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <BiometricIcon><img src="images/login/voiceLogin.jpg" alt="Voice" width={70} height={70} /></BiometricIcon>
              <BiometricLabel>Voice</BiometricLabel>
            </BiometricOption>
            
            <BiometricOption 
              active={authMethod === 'face' ? "true" : "false"}
              onClick={() => setAuthMethod('face')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <BiometricIcon><img src="images/login/faceLogin.jpg" alt="Face" width={70} height={70} /></BiometricIcon>
              <BiometricLabel>Face</BiometricLabel>
            </BiometricOption>
            
            <BiometricOption 
              active={authMethod === 'pin' ? "true" : "false"}
              onClick={() => setAuthMethod('pin')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <BiometricIcon><img src="images/login/pinLogin.jpg" alt="Pin" width={70} height={70} /></BiometricIcon>
              <BiometricLabel>PIN</BiometricLabel>
            </BiometricOption>
            
            <BiometricOption 
              active={authMethod === 'password' ? "true" : "false"}
              onClick={() => setAuthMethod('password')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <BiometricIcon><img src="images/login/passwordLogin.jpg" alt="Password" width={70} height={70} /></BiometricIcon>
              <BiometricLabel>Password</BiometricLabel>
            </BiometricOption>
          </BiometricOptionsContainer> */}


          {/* Biometric container */}
          <BiometricContainer>
            {/* Voice authentication */}
            {authMethod === 'voice' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <BiometricCircle>
                  <BiometricIcon><img src="images/login/voiceLogin.jpg" alt="Voice" width={70} height={70} style={{ mixBlendMode: 'multiply' }} /></BiometricIcon>
                </BiometricCircle>

                <StatusText style={{ marginTop: '1rem' }}>
                  {status || 'Click the button below to start voice authentication'}
                </StatusText>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  <Button
                    onClick={startVoiceRecognition}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      marginTop: '1rem',
                      display: isVoiceRecording ? 'none' : 'block',
                      width: '100%',
                      maxWidth: '250px'
                    }}
                  >
                    Start Voice Scan
                  </Button>

                  <Button
                    onClick={() => {
                      if (audioStream) {
                        audioStream.getTracks().forEach(track => track.stop());
                      }
                      setIsVoiceRecording(false);
                      setStatus('Voice recognition cancelled');
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      marginTop: '1rem',
                      background: 'rgba(255, 0, 0, 0.7)',
                      display: isVoiceRecording ? 'block' : 'none',
                      width: '100%',
                      maxWidth: '250px'
                    }}
                  >
                    Stop Recording
                  </Button>
                </div>

                <div style={{
                  marginTop: '1rem',
                  fontSize: '0.8rem',
                  color: 'rgba(0, 0, 0, 0.6)',
                  textAlign: 'center',
                  maxWidth: '300px'
                }}>
                  {isVoiceRecording ?
                    "Please speak clearly. Voice recognition in progress..." :
                    ""
                  }
                </div>
              </motion.div>
            )}

            {/* Face authentication */}
            {authMethod === 'face' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <BiometricCircle style={{ overflow: 'hidden', position: 'relative' }}>
                  {isFaceScanning ? (
                    <>
                      <VideoFeed ref={videoRef} autoPlay playsInline muted />
                      <FaceGrid>
                        {Array(9).fill().map((_, i) => (
                          <GridCell key={i} />
                        ))}
                      </FaceGrid>
                      <motion.div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '2px',
                          background: 'rgba(0, 102, 255, 0.8)',
                          zIndex: 5
                        }}
                        animate={{
                          top: ['0%', '100%', '0%'],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: 'linear'
                        }}
                      />
                    </>
                  ) : (
                    <BiometricIcon><img src="images/login/faceLogin.jpg" alt="Face" width={70} height={70} style={{ mixBlendMode: 'multiply' }} /></BiometricIcon>
                  )}
                </BiometricCircle>

                <StatusText style={{ marginTop: '1rem' }}>
                  {status || 'Click the button below to scan your face'}
                </StatusText>

                <Button
                  onClick={startFaceRecognition}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    marginTop: '1rem',
                    display: isFaceScanning ? 'none' : 'block',
                    width: '100%',
                    maxWidth: '250px'
                  }}
                >
                  Start Face Scan
                </Button>

                <Button
                  onClick={() => {
                    if (videoStream) {
                      videoStream.getTracks().forEach(track => track.stop());
                    }
                    setIsFaceScanning(false);
                    setStatus('Face scanning cancelled');
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    marginTop: '1rem',
                    background: 'rgba(255, 0, 0, 0.7)',
                    display: isFaceScanning ? 'block' : 'none',
                    width: '100%',
                    maxWidth: '250px'
                  }}
                >
                  Cancel Scan
                </Button>

                <div style={{
                  marginTop: '1rem',
                  fontSize: '0.8rem',
                  color: 'rgba(0, 0, 0, 0.6)',
                  textAlign: 'center',
                  maxWidth: '300px'
                }}>
                  {isFaceScanning ?
                    "Please look directly at the camera. Face recognition in progress..." :
                    "Click the button to start face authentication. You'll need to look directly at the camera."
                  }
                </div>
              </motion.div>
            )}

            {/* PIN authentication */}
            {authMethod === 'pin' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <PinContainer>
                  {pinDigits.map((digit, index) => (
                    <PinInput
                      key={index}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handlePinChange(index, e.target.value)}
                      onKeyDown={(e) => handlePinKeyDown(index, e)}
                      ref={(el) => pinRefs.current[index] = el}
                      autoFocus={index === 0}
                    />
                  ))}
                </PinContainer>

                <Button
                  onClick={() => {
                    if (pinDigits.every(digit => digit !== '')) {
                      setStatus('Verifying PIN...');

                      // Simulate PIN verification
                      setTimeout(() => {
                        setStatus('PIN verified!');
                        handleLogin();
                      }, 1500);
                    } else {
                      setStatus('Please enter all 6 digits');
                    }
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={!pinDigits.every(digit => digit !== '')}
                >
                  Enter Login PIN
                </Button>

                <StatusText>{status}</StatusText>
              </motion.div>
            )}

            {/* Password authentication */}
            {authMethod === 'password' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <Form onSubmit={handleLoginSubmit}>
                  <InputGroup>
                    <Label>Username</Label>
                    <Input
                      type="text"
                      placeholder="Enter your username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </InputGroup>

                  <InputGroup>
                    <Label>Password</Label>
                    <Input
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </InputGroup>

                  <Button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Login
                  </Button>
                </Form>

                <StatusText>{status}</StatusText>
              </motion.div>
            )}

            {/* Fingerprint authentication */}
            {authMethod === 'fingerprint' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <BiometricCircle
                  onClick={() => {
                    setStatus('Scanning fingerprint...');

                    // Simulate fingerprint scanning
                    setTimeout(() => {
                      setStatus('Fingerprint recognized');
                      setTimeout(() => {
                        handleLogin();
                      }, 2000);
                    }, 2000);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{ cursor: 'pointer' }}
                >
                  <BiometricIcon><img src="images/login/pinLogin.jpg" alt="Pin" width={70} height={70} style={{ mixBlendMode: 'multiply' }} /></BiometricIcon>
                </BiometricCircle>

                <StatusText style={{ marginTop: '1rem' }}>
                  {status || 'Touch to scan your fingerprint'}
                </StatusText>
              </motion.div>
            )}

            {/* Token authentication */}
            {authMethod === 'token' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <Form onSubmit={handleLoginSubmit}>
                  <InputGroup>
                    <Label>Token</Label>
                    <Input type="text" placeholder="Enter your access token" required />
                  </InputGroup>

                  <Button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Verify Token
                  </Button>
                </Form>

                <StatusText>{status}</StatusText>
              </motion.div>
            )}
          </BiometricContainer>
          {/* <MmsOauthWrapper
            code={authParams.code}
            dummyval={true}
            isreact={true}
            provider={authParams.provider}
            state={authParams.state}
            applicationid={authParams.applicationId}
            appsource={authParams.applicationSource}
            onCallbackResponse={handleCallbackResponse}
            onGraphClicked={handleProviderClick}
          /> */}
          {oauthProviders?.length > 0 && (
          <OauthProviders>
            <div class="divider">
              <span class="divider-line"></span>
              <span class="divider-text"> Or Login using </span>
              <span class="divider-line"></span>
            </div>
            <div class="option">
              {oauthProviders.map((provider) => (
                <a
                  key={provider.server_name}
                  className={`oauth-button ${provider.server_name.toLowerCase()}`}
                  onClick={() => handleProviderClick(provider)}
                >
                  <img 
                    src={`images/oauthConnectors/${provider.server_name.toLowerCase()}.svg`} 
                    alt={provider.server_name} 
                    width={20}
                  />
                  {`Login with ${provider.server_name.charAt(0).toUpperCase() + provider.server_name.slice(1).toLowerCase()}`}
                </a>
              ))}
            </div>

          </OauthProviders>
          )}
          {/* <AlternativeLogin>
            <p>Forgot your credentials?</p>
            <button onClick={() => navigate('/reset-password')}>Reset Password</button>
            <button onClick={() => navigate('/support')}>Contact support</button>
          </AlternativeLogin> */}

          <Footer>
            <span>&copy; {new Date().getFullYear()} BlueMorMon. All rights reserved.</span>
            <Copyright>Powered by MetaMorphoSys Technologies v3</Copyright>
          </Footer>
        </LoginSection>
      </LoginContainer>

      {/* Two-Factor Authentication Modal */}
      {showTwoFactor && (
        <TwoFactorModal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <TwoFactorCard
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <TwoFactorTitle>Two-Factor Authentication</TwoFactorTitle>
            <TwoFactorDescription>
              Please enter the verification code from your authenticator app or device.
            </TwoFactorDescription>

            <TabContainer style={{ marginBottom: '1.5rem' }}>
              <Tab
                active={twoFactorMethod === 'app' ? "true" : "false"}
                onClick={() => setTwoFactorMethod('app')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Authenticator App
              </Tab>
              <Tab
                active={twoFactorMethod === 'sms' ? "true" : "false"}
                onClick={() => setTwoFactorMethod('sms')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                SMS
              </Tab>
              <Tab
                active={twoFactorMethod === 'email' ? "true" : "false"}
                onClick={() => setTwoFactorMethod('email')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Email
              </Tab>
            </TabContainer>

            <Form onSubmit={handleTwoFactorSubmitUpdated} style={{ width: '100%' }}>
              <InputGroup>
                <Label>Verification Code</Label>
                <Input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  maxLength={6}
                  pattern="[0-9]*"
                  inputMode="numeric"
                  autoFocus
                />
              </InputGroup>

              <StatusText style={{ marginTop: '0.5rem', marginBottom: '1rem', textAlign: 'center' }}>
                {twoFactorMethod === 'app' && 'Open your authenticator app to view the code'}
                {twoFactorMethod === 'sms' && 'A code has been sent to your registered phone'}
                {twoFactorMethod === 'email' && 'A code has been sent to your registered email'}
              </StatusText>

              <Button
                type="submit"
                disabled={twoFactorCode.length !== 6}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ width: '100%' }}
              >
                Verify
              </Button>

              <Button
                type="button"
                onClick={() => setShowTwoFactor(false)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  width: '100%',
                  marginTop: '0.5rem',
                  background: 'transparent',
                  color: 'rgba(0, 102, 255, 0.8)',
                  border: '1px solid rgba(0, 102, 255, 0.3)'
                }}
              >
                Cancel
              </Button>
            </Form>
          </TwoFactorCard>
        </TwoFactorModal>
      )}
    </Container>
  );
};

export default Login;
